'use client';

import { motion } from 'motion/react';
import Image from 'next/image';

import { TextReveal } from '@/components/elements/text-reveal';

interface Feature {
  title: string;
  description: string;
  image: string;
}

interface FeaturePairProps {
  feature1: Feature;
  feature2: Feature;
}

function FeatureText({ feature }: { feature: Feature }) {
  return (
    <div>
      <TextReveal mode="animate">
        <h3 className="font-display max-w-[420px] text-2xl leading-tight tracking-tight md:text-[32px] md:leading-10">
          {feature.title}
        </h3>
      </TextReveal>
      <p className="text-muted-foreground mt-4 max-w-[420px] text-base leading-[27px]">
        {feature.description}
      </p>
    </div>
  );
}

export function FeaturePair({ feature1, feature2 }: FeaturePairProps) {
  return (
    <section className="section-padding overflow-hidden">
      <div className="container flex flex-col gap-20 md:gap-28">
        {/* Feature 1 — visual left, text right */}
        <motion.div
          className="grid items-center gap-12 md:grid-cols-2 md:gap-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col items-center">
            <div className="w-full max-w-[290px] -rotate-3 rounded-[18px] bg-white p-5 shadow-[0px_13px_29px_rgba(0,0,0,0.12)]">
              <Image
                src={feature1.image}
                alt={feature1.title}
                width={500}
                height={500}
                unoptimized={feature1.image.endsWith('.svg')}
                className="aspect-square w-full object-contain"
              />
            </div>
            <span
              aria-hidden
              className="font-handwritten text-text-secondary mt-6 self-start pl-[15%] text-2xl"
            >
              try now
            </span>
          </div>
          <FeatureText feature={feature1} />
        </motion.div>

        {/* Feature 2 — text left, visual right */}
        <motion.div
          className="grid items-center gap-12 md:grid-cols-2 md:gap-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="order-2 flex justify-center md:order-1 md:justify-start">
            <FeatureText feature={feature2} />
          </div>
          <div className="order-1 flex justify-center md:order-2">
            <Image
              src={feature2.image}
              alt={feature2.title}
              width={500}
              height={1000}
              unoptimized={feature2.image.endsWith('.svg')}
              className="h-auto w-full max-w-[280px] object-contain drop-shadow-[0px_12px_26px_rgba(0,0,0,0.14)]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
